import { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { LIMITS } from "@shared/commission";
import { useCommissionStore } from "../../stores/commissionStore";
import { useUploadStore } from "../../stores/uploadStore";
import { useIntakeCopy } from "../../i18n/useIntakeCopy";
import { pickNudge } from "../../lib/nudges";
import { staggerChildren, riseItem, DUR, EASE_ENTER } from "../../motion/tokens";
import { RefDropzone } from "../RefDropzone";
import { Nudge } from "../Nudge";
import { StepNav } from "../StepNav";
import { BLOCK_KEYS, type BlockDraft, type CharacterDraft, type IntakeArtist } from "../../types";

const input = "w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-[var(--color-text)] outline-none focus:border-[var(--color-primary)]";
const has = (b: BlockDraft) => b.text.trim().length > 0 || b.refs.length > 0;
const refCount = (c: CharacterDraft) => c.look.refs.length + c.action.refs.length + c.extra.refs.length;

export function CharactersStep({ artist }: { artist: IntakeArtist }) {
  const { c, fmt } = useIntakeCopy();
  const s = useCommissionStore();
  const pendingUploads = useUploadStore((u) => u.pending);
  const [notesOpen, setNotesOpen] = useState(s.notes.length > 0);
  const chars = s.characters.slice(0, s.characterCount);
  const nudge = pickNudge(s, "characters");
  // Guide, never limit: pictures or words, in any block, are enough to continue.
  const canNext = chars.every((ch) => BLOCK_KEYS.some((k) => has(ch[k]))) && pendingUploads === 0;

  return (
    <section className="space-y-6">
      <div>
        <h2 data-step-heading tabIndex={-1} className="font-(family-name:--font-display) font-normal text-3xl text-[var(--color-text)] outline-none">{c.characters.question}</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">{fmt(c.characters.hint, { artist: artist.name })}</p>
      </div>

      <AnimatePresence mode="popLayout">{nudge && <Nudge key={nudge} id={nudge} />}</AnimatePresence>

      <m.div variants={staggerChildren(0.06)} initial="hidden" animate="show" className="space-y-4">
        {chars.map((ch, i) => (
          <m.fieldset key={ch.id} variants={riseItem} layout
            className="space-y-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4 focus-within:border-[var(--color-primary)]/60 sm:p-5">
            <legend className="px-1 font-(family-name:--font-display) font-normal text-xl text-[var(--color-primary-light)]">
              {`${fmt(c.characters.card, { n: i + 1 })} · ${ch.type ?? ""}`}
            </legend>
            <input className={input} maxLength={LIMITS.name} placeholder={c.characters.name} aria-label={c.characters.name}
              value={ch.name} onChange={(e) => s.updateCharacter(ch.id, { name: e.target.value })} />
            {BLOCK_KEYS.map((k) => {
              const b = c.characters.blocks[k];
              const room = LIMITS.maxRefsPerCharacter - refCount(ch) + ch[k].refs.length;
              return (
                <div key={k} className="space-y-2">
                  <label className="block font-medium text-[var(--color-text)]" htmlFor={`${ch.id}-${k}`}>{b.title}</label>
                  <textarea id={`${ch.id}-${k}`} className={`${input} min-h-20`} maxLength={LIMITS.blockText}
                    placeholder={fmt(b.placeholder, { artist: artist.name })}
                    value={ch[k].text} onChange={(e) => s.setBlockText(ch.id, k, e.target.value)} />
                  <RefDropzone refs={ch[k].refs} max={Math.max(ch[k].refs.length, room)} label={c.characters.dropRefs} compact
                    onAdd={(url) => s.addBlockRef(ch.id, k, url)} onRemove={(url) => s.removeBlockRef(ch.id, k, url)} />
                </div>
              );
            })}
          </m.fieldset>
        ))}

        {s.backgroundType === "complex" && (
          <m.fieldset variants={riseItem} layout className="space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4 sm:p-5">
            <legend className="px-1 font-(family-name:--font-display) font-normal text-xl text-[var(--color-primary-light)]">{c.characters.scene}</legend>
            <textarea className={`${input} min-h-16`} maxLength={LIMITS.sceneDescription} placeholder={c.characters.scenePlaceholder} aria-label={c.characters.sceneDescription}
              value={s.sceneDescription} onChange={(e) => s.setSceneDescription(e.target.value)} />
            <RefDropzone refs={s.sceneRefs} max={LIMITS.maxSceneRefs} label={c.characters.dropRefs} compact
              onAdd={s.addSceneRef} onRemove={s.removeSceneRef} />
          </m.fieldset>
        )}
      </m.div>

      <div>
        <button type="button" onClick={() => setNotesOpen((o) => !o)} aria-expanded={notesOpen}
          className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
          <m.span animate={{ rotate: notesOpen ? 180 : 0 }}><ChevronDown className="h-4 w-4" /></m.span>{c.characters.notes}
        </button>
        <AnimatePresence initial={false}>
          {notesOpen && (
            <m.textarea initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0, transition: { duration: DUR.ui, ease: EASE_ENTER } }} exit={{ opacity: 0 }}
              className={`${input} mt-2 min-h-24`} maxLength={LIMITS.notes} placeholder={c.characters.notesPlaceholder} aria-label={c.characters.notes}
              value={s.notes} onChange={(e) => s.setNotes(e.target.value)} />
          )}
        </AnimatePresence>
      </div>

      <StepNav canNext={canNext} />
    </section>
  );
}
